/**
 * <interactive-size-guide> — "Find your dog's size": a chest slider that reads out the fitting size.
 *
 * The markup and the size data are snippets/dm-size-guide-body.liquid, printed inside the element by
 * dm-size-guide (standard PDP) and variant-main-picker (legacy template). This script finds its parts
 * by their size-guide__ class names and needs nothing from the page around it.
 */
if (!customElements.get('interactive-size-guide')) {
  class InteractiveSizeGuide extends HTMLElement {
    constructor() {
      super();
      this.data = [];
      this.currentValue = 15;
      this.currentIndex = 4;
      this.currentUnit = 'in';
      this.changingTimeout = null;
    }

    connectedCallback() {
      const scriptEl = this.querySelector('.size-guide-data');
      if (scriptEl) {
        try {
          this.data = JSON.parse(scriptEl.textContent);
        } catch (e) {
          console.error('Failed to parse size guide data', e);
        }
      }

      this.slider = this.querySelector('.size-guide__slider');
      this.unitButtons = this.querySelectorAll('.size-guide__unit-button');
      this.trackFill = this.querySelector('.size-guide__track-fill');
      this.thumb = this.querySelector('.size-guide__slider-thumb');
      this.resultCard = this.querySelector('.size-guide__result-card');
      this.sizeLabel = this.querySelector('.size-guide__size-label');
      this.breedLabel = this.querySelector('.size-guide__breed-label');
      this.chestValue = this.querySelector('[data-value="chest"]');
      this.collarValue = this.querySelector('[data-value="collar"]');
      this.measurementValue = this.querySelector('[data-value="measurement"]');
      this.betweenNudge = this.querySelector('.size-guide__between-nudge');

      if (this.slider && this.data.length > 0) {
        this.currentValue = parseFloat(this.slider.value);
        this.setupTrackMarks();
        this.slider.addEventListener('input', (e) => {
          this.currentValue = parseFloat(e.target.value);
          this.updateDisplay();
        });
      }

      this.unitButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.currentUnit = button.dataset.unit || 'in';
          this.unitButtons.forEach((unitButton) => {
            const isActive = unitButton === button;
            unitButton.classList.toggle('active', isActive);
            unitButton.setAttribute('aria-pressed', String(isActive));
          });
          this.updateDisplay();
        });
      });

      if (this.data.length > 0) {
        this.updateDisplay();
      }
    }

    setupTrackMarks() {
      const marksContainer = this.querySelector('.size-guide__track-marks');
      if (!marksContainer) return;

      marksContainer.innerHTML = '';
      this.data.forEach((item, index) => {
        const mark = document.createElement('button');
        const midpoint = (item.chestMin + item.chestMax) / 2;
        mark.type = 'button';
        mark.className = 'size-guide__mark dm-hit';
        mark.textContent = item.label;
        mark.setAttribute('aria-label', item.label);
        mark.addEventListener('click', () => {
          this.slider.value = midpoint;
          this.currentValue = midpoint;
          this.updateDisplay();
        });
        marksContainer.appendChild(mark);
      });
    }

    getSizeIndex(value) {
      const index = this.data.findIndex((item) => value <= item.chestMax);
      return index === -1 ? this.data.length - 1 : index;
    }

    formatRange(min, max) {
      if (this.currentUnit === 'cm') {
        return `${(min * 2.54).toFixed(1)}-${(max * 2.54).toFixed(1)} cm`;
      }

      return null;
    }

    formatMeasurement(value) {
      if (this.currentUnit === 'cm') {
        return `${(value * 2.54).toFixed(1)} cm`;
      }

      return `${value.toFixed(1)} in`;
    }

    updateSliderProgress() {
      const min = parseFloat(this.slider.min);
      const max = parseFloat(this.slider.max);
      const percent = ((this.currentValue - min) / (max - min)) * 100;
      const clampedPercent = Math.max(0, Math.min(100, percent));

      if (this.trackFill) {
        this.trackFill.style.width = `${clampedPercent}%`;
      }

      if (this.thumb) {
        this.thumb.style.left = `calc(${clampedPercent}% + ${(1 - clampedPercent / 50) * 12}px)`;
      }
    }

    pulseResultCard(nextIndex) {
      if (!this.resultCard || this.currentIndex === nextIndex) return;

      window.clearTimeout(this.changingTimeout);
      this.resultCard.classList.add('changing');
      this.changingTimeout = window.setTimeout(() => {
        this.resultCard.classList.remove('changing');
      }, 180);
    }

    updateDisplay() {
      const nextIndex = this.getSizeIndex(this.currentValue);
      const item = this.data[nextIndex];
      if (!item) return;

      this.pulseResultCard(nextIndex);
      this.currentIndex = nextIndex;
      this.updateSliderProgress();

      const marks = this.querySelectorAll('.size-guide__mark');
      marks.forEach((mark, i) => {
        if (i === nextIndex) mark.classList.add('active');
        else mark.classList.remove('active');
      });

      if (this.sizeLabel) this.sizeLabel.textContent = item.label;
      if (this.breedLabel) this.breedLabel.textContent = item.breed;
      if (this.chestValue) this.chestValue.textContent = this.formatRange(item.chestMin, item.chestMax) || item.chest;
      if (this.collarValue) this.collarValue.textContent = this.formatRange(item.collarMin, item.collarMax) || item.collar;
      if (this.measurementValue) this.measurementValue.textContent = this.formatMeasurement(this.currentValue);

      if (this.betweenNudge) {
        const isNearUpperBoundary = nextIndex < this.data.length - 1 && Math.abs(this.currentValue - item.chestMax) < 0.8;
        this.betweenNudge.hidden = !isNearUpperBoundary;
      }
    }
  }

  customElements.define('interactive-size-guide', InteractiveSizeGuide);
}
